// The client changes bar under the advisor's client tabs (#739): field labels,
// what each day's changes read as, the track's span and ticks, and where the dots
// go. Spec: docs/superpowers/specs/2026-10-26-client-changes-timeline-739-design.md.
//
// Days arrive from /api/consults/survey/<id>/changes/ as 'YYYY-MM-DD' Eastern
// calendar days, already the client's own net changes. Dates are handled in UTC
// here so the viewer's timezone never moves a day.
import _ from 'lodash'
import moment from 'moment'

export const KIND_COLORS = { drugs: '#d63384', doctors: '#0d6efd', pharmacies: '#198754', profile: '#6f42c1', read: '#ced4da' }
export const KIND_ORDER = ['drugs', 'doctors', 'pharmacies', 'profile']
export const KIND_ICONS = { drugs: 'prescription-bottle-alt', doctors: 'user-md', pharmacies: 'store', profile: 'user' }
export const SOURCE_LABELS = { aep_survey: 'AEP survey', checkup: 'Checkup', scheduler: 'Scheduler', profile: 'Profile' }
// AEP survey days stand out from Checkup / Profile edits on the Quick AEP page
export const isAepDay = d => (d.sources || []).includes('aep_survey')

const GROUPS = {
    supp_plan: 'Current Medigap plan',
    pdp_plan: 'Current drug plan',
    mapd_plan: 'Current Medicare Advantage plan',
    address: 'Address',
    mailing_address: 'Mailing address',
}
const PLAN_GROUPS = ['supp_plan', 'pdp_plan', 'mapd_plan']

// tab: the client tab the field lives on, '' = Profile
const field = (label, tab, extra = {}) => ({ label, kind: 'profile', tab, ...extra })
const demographics = (label, extra) => field(label, 'demographics', extra)
const enrollment = (label, extra) => field(label, 'enrollment-period', extra)
const income = (label, extra) => field(label, 'income-surcharges', extra)
const health = (label, extra) => field(label, 'health-lifestyle', extra)

export const FIELD_META = {
    drugs: { label: 'Drugs', kind: 'drugs', tab: 'drugs' },
    no_drugs: { label: 'Takes no drugs', kind: 'drugs', tab: 'drugs' },
    providers: { label: 'Doctors', kind: 'doctors', tab: 'providers' },
    hospitals: { label: 'Hospitals', kind: 'doctors', tab: 'providers' },
    no_providers: { label: 'No doctors to keep', kind: 'doctors', tab: 'providers' },
    no_hospitals: { label: 'No hospitals to keep', kind: 'doctors', tab: 'providers' },
    preferred_hospital: { label: 'Preferred hospital', kind: 'doctors', tab: 'providers' },
    pharmacies: { label: 'Pharmacies', kind: 'pharmacies', tab: 'pharmacies' },
    no_pharmacies: { label: 'No pharmacy preference', kind: 'pharmacies', tab: 'pharmacies' },
    other_pharmacies_ok: { label: 'Other pharmacies OK', kind: 'pharmacies', tab: 'pharmacies' },
    prefer_mail_order: { label: 'Prefers mail order', kind: 'pharmacies', tab: 'pharmacies' },

    salutation: demographics('Salutation'),
    first_name: demographics('First name'),
    middle_name: demographics('Middle name'),
    last_name: demographics('Last name'),
    suffix: demographics('Suffix'),
    preferred_name: demographics('Preferred name'),
    dob: demographics('Date of birth'),
    manual_age: demographics('Age'),
    gender: demographics('Gender'),
    email: demographics('Email'),
    work_email: demographics('Work email'),
    cell_phone: demographics('Cell phone'),
    home_phone: demographics('Home phone'),
    sms_opt_in: demographics('Texts OK'),
    email_opt_in: demographics('Emails OK'),
    street_1: demographics('Street', { group: 'address' }),
    street_2: demographics('Street line 2', { group: 'address' }),
    city: demographics('City', { group: 'address' }),
    state: demographics('State', { group: 'address' }),
    zip_code: demographics('Zip code'),
    mailing_address_same_as_permanent: demographics('Mailing address same as home'),
    mailing_street_1: demographics('Mailing street', { group: 'mailing_address' }),
    mailing_street_2: demographics('Mailing street line 2', { group: 'mailing_address' }),
    mailing_city: demographics('Mailing city', { group: 'mailing_address' }),
    mailing_state: demographics('Mailing state', { group: 'mailing_address' }),
    mailing_zip_code: demographics('Mailing zip code', { group: 'mailing_address' }),
    group_relationship: demographics('Relationship to the employee'),
    group_plan_name: demographics('Group plan'),
    source_type: demographics('Heard about us from'),
    source_name: demographics('Referred by'),
    source_opt_out: demographics('Prefers not to say how they heard'),
    va: demographics('VA benefits'),
    tricare: demographics('TRICARE'),
    champva: demographics('CHAMPVA'),
    fep_blue: demographics('FEP Blue'),
    medicaid: demographics('Medicaid'),
    hsa: demographics('HSA'),
    household_discount: demographics('Household discount'),
    reassurance: demographics('Reassurance'),

    enrollment_period: enrollment('Enrollment period'),
    sep_reason: enrollment('Special enrollment reason'),
    effective_date: enrollment('Effective date'),
    coverage_urgency: enrollment('Coverage urgency'),
    current_medicare_coverage: enrollment('Current Medicare coverage'),
    current_plans_year: enrollment('Current plans year'),
    medicare_number: enrollment('Medicare number'),
    medicare_parts: enrollment('Medicare parts'),
    part_a_date: enrollment('Part A date'),
    part_b_date: enrollment('Part B date'),
    collecting_ssi: enrollment('Collecting Social Security'),
    collecting_rri: enrollment('Collecting Railroad Retirement'),
    disabled: enrollment('Disabled'),
    disabled_gt24m: enrollment('Disabled over 24 months'),
    employer_coverage_p65: enrollment('Employer coverage past 65'),
    employer_size_gt20: enrollment('Employer has over 20 employees'),
    employer_size_gt100: enrollment('Employer has over 100 employees'),
    working_p65: enrollment('Working past 65'),
    retired: enrollment('Retired'),
    retiring: enrollment('Retiring'),
    us_citizen: enrollment('US citizen'),
    us_resident: enrollment('US resident'),
    pre_medicare_coverage: enrollment('Coverage before Medicare'),
    pre_medicare_carrier: enrollment('Carrier before Medicare'),
    pre_medicare_plan: enrollment('Plan before Medicare'),
    pre_medicare_end_date: enrollment('Coverage before Medicare ends'),
    supp_carrier: enrollment('Medigap carrier', { group: 'supp_plan' }),
    supp_name: enrollment('Medigap plan', { group: 'supp_plan' }),
    supp_plan_id: enrollment('Medigap plan id', { group: 'supp_plan' }),
    pdp_carrier: enrollment('Drug plan carrier', { group: 'pdp_plan' }),
    pdp_name: enrollment('Drug plan', { group: 'pdp_plan' }),
    pdp_plan_id: enrollment('Drug plan id', { group: 'pdp_plan' }),
    mapd_carrier: enrollment('Medicare Advantage carrier', { group: 'mapd_plan' }),
    mapd_name: enrollment('Medicare Advantage plan', { group: 'mapd_plan' }),
    mapd_plan_id: enrollment('Medicare Advantage plan id', { group: 'mapd_plan' }),

    current_income: income('Current income'),
    projected_income: income('Projected income'),
    tax_filing_status: income('Tax filing status'),
    projected_tax_filing_status: income('Projected tax filing status'),
    lives_with_spouse: income('Lives with spouse'),
    projected_lives_with_spouse: income('Will live with spouse'),
    tax_quarters_paid: income('Quarters of Medicare tax paid'),

    chronic_condition: health('Chronic condition'),
    chronic_conditions: health('Chronic conditions'),
    als: health('ALS'),
    cardiovascular_disorders: health('Cardiovascular disorders'),
    chronic_disabling_mental_health: health('Chronic disabling mental health condition'),
    chronic_heart_failure: health('Chronic heart failure'),
    chronic_lung_disorders: health('Chronic lung disorders'),
    dementia: health('Dementia'),
    diabetes: health('Diabetes'),
    esrd: health('ESRD'),
    hiv_aids: health('HIV/AIDS'),
    hcs: health('Treated for cancer, heart disease or stroke'),
    health_changes: health('Health changes'),
    dental_health: health('Dental health'),
    situation: health('Health situation notes'),
    tobacco: health('Tobacco'),
    upcoming_procedure: health('Upcoming procedure'),
    upcoming_procedures: health('Upcoming procedures'),
    other_healthcare_required: health('Other healthcare needed'),
    require_dental: health('Needs dental'),
    require_vision: health('Needs vision'),
    require_hearing: health('Needs hearing'),
    require_other_healthcare: health('Needs other healthcare'),
    preferred_plan_class: health('Preferred plan type'),
    moving: health('Moving'),
    moving_date: health('Moving date'),
    moving_state: health('Moving to state'),
    moving_zipcode: health('Moving to zip code'),
    multiple_states: health('Lives in more than one state'),
    travel_target: health('Travels to'),

    feedback_general: field('Feedback', '', { feedback: true }),
    feedback_medical: field('Feedback on medical', '', { feedback: true }),
    feedback_prescription: field('Feedback on prescriptions', '', { feedback: true }),
    happy_medical: field('Happy with medical coverage', ''),
    happy_prescription: field('Happy with drug coverage', ''),
    medigap_review_requested: field('Medigap review requested', ''),
    deceased: field('Deceased', ''),
    skip_soa: field('Skip SOA', ''),
}

export function fieldMeta (name) {
    return FIELD_META[name] || { label: _.startCase(name), kind: 'profile', tab: '' }
}

const day = s => moment.utc(s, 'YYYY-MM-DD')

function formatValue (value) {
    if (value === null || value === undefined || value === '') return 'cleared'
    if (value === true) return 'Yes'
    if (value === false) return 'No'
    if (Array.isArray(value)) return value.map(formatValue).join(', ')
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return day(value).format('MMM D, YYYY')
    // A chronic condition or an upcoming procedure
    if (_.isPlainObject(value)) return value.primary_name || value.description || value.name || value.label || 'updated'
    return String(value)
}

function lineItems (line) {
    const changes = line.changes
    if (PLAN_GROUPS.includes(line.key)) {
        const byField = _.keyBy(changes, 'field')
        const parts = ['carrier', 'name'].map(part => _.find(byField, (c, f) => f.endsWith(`_${part}`))).filter(c => c && c.value)
        return [{ text: parts.map(c => c.value).join(' · ') || 'updated', variant: 'set' }]
    }
    if (line.key === 'address' || line.key === 'mailing_address') {
        // Where the client moved to, and from: each part with its own label
        return changes.map(c => ({
            text: `${fieldMeta(c.field).label} ${formatValue(c.value)}`,
            variant: 'set',
            note: c.has_previous ? `was ${formatValue(c.previous)}` : undefined,
        }))
    }
    return _.flatMap(changes, (c) => {
        if (c.type === 'list') {
            // Before unknown (an earlier save still in S3): what the list is now, not what was added
            const variant = c.known_before === false ? 'set' : 'add'
            return [
                ...c.added.map(i => ({ text: i.detail ? `${i.label} · ${i.detail}` : i.label, variant })),
                ...c.removed.map(i => ({ text: i.label, variant: 'del' })),
                ...c.changed.map(i => ({ text: `${i.label}: ${i.from} → ${i.to}`, variant: 'set' })),
            ]
        }
        if (c.type === 'masked') return [{ text: 'updated', variant: 'set' }]
        if (line.feedback) return [{ text: 'left feedback', variant: 'note', note: c.value }]
        return [{ text: formatValue(c.value), variant: 'set', note: c.has_previous ? `was ${formatValue(c.previous)}` : undefined }]
    })
}

function lineChips (line) {
    if (line.feedback) return []
    const c = line.changes[0]
    if (c.type === 'list') {
        return [
            ...c.added.map(i => (c.known_before === false ? { text: i.label, variant: 'set' } : { text: `+${i.label}`, variant: 'add' })),
            ...c.removed.map(i => ({ text: i.label, variant: 'del' })),
            ...c.changed.map(i => ({ text: i.label, variant: 'set' })),
        ]
    }
    if (line.key === 'zip_code') return [{ text: `Zip ${c.value}`, variant: 'set' }]
    return [{ text: line.label, variant: 'set' }]
}

export function presentDay (d) {
    const lines = []
    const byKey = {}
    for (const change of d.changes) {
        const meta = fieldMeta(change.field)
        const key = meta.group || change.field
        if (!byKey[key]) {
            byKey[key] = { key, label: meta.group ? GROUPS[meta.group] : meta.label, kind: meta.kind, tab: meta.tab, feedback: !!meta.feedback, changes: [] }
            lines.push(byKey[key])
        }
        byKey[key].changes.push(change)
    }
    const sorted = _.sortBy(lines, [l => KIND_ORDER.indexOf(l.kind), 'label'])
    sorted.forEach((l) => { l.items = lineItems(l) })
    const chips = _.flatMap(sorted, lineChips)
    if (sorted.some(l => l.feedback)) chips.push({ text: 'left feedback', variant: 'note' })
    return {
        ...d,
        lines: sorted,
        chips,
        count: chips.length,
        kind: KIND_ORDER.find(k => sorted.some(l => l.kind === k)) || 'profile',
    }
}

export function trackSpan (days, today) {
    const end = day(today)
    const oldest = moment.min(days.map(d => day(d.day)))
    const pad = Math.max(1, Math.round(0.03 * end.diff(oldest, 'days')))
    const start = moment.min(oldest.clone().subtract(pad, 'days'), end.clone().subtract(7, 'days'))
    const total = end.diff(start, 'days')
    const pos = m => m.diff(start, 'days') / total

    const ticks = []
    let m
    let step
    let label
    if (total <= 14) {
        m = start.clone().add(2, 'days')
        step = () => m.add(2, 'days')
        label = () => m.format('MMM D')
    } else if (total <= 90) {
        m = start.clone().isoWeekday(1)
        if (!m.isAfter(start)) m.add(1, 'week')
        step = () => m.add(1, 'week')
        label = () => m.format('MMM D')
    } else {
        m = start.clone().startOf('month').add(1, 'month')
        step = () => m.add(1, 'month')
        label = () => m.format(m.month() === 0 ? "MMM 'YY" : 'MMM')
    }
    for (; m.isBefore(end); step()) {
        if (pos(m) <= 0.96) ticks.push({ label: label(), pos: pos(m) })
    }
    ticks.push({ label: 'Today', pos: 1 })
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD'), ticks }
}

export function placeDots (days, span, widthPx) {
    const start = day(span.start)
    const total = day(span.end).diff(start, 'days')
    const clusters = []
    for (const d of _.sortBy(days, 'day')) {
        const pos = day(d.day).diff(start, 'days') / total
        const px = pos * widthPx
        const last = _.last(clusters)
        if (last && px - last.px < 16) {
            last.days.push(d)
            last.px = px
            last.pos = pos
        } else {
            clusters.push({ days: [d], px, pos })
        }
    }
    return clusters.map(({ days: clustered, pos }) => {
        const presented = clustered.map(presentDay)
        const read = clustered.every(d => !!d.read)
        const unread = presented.filter(p => !p.read)
        const pool = unread.length ? unread : presented
        const count = presented[0].count
        return {
            days: clustered,
            pos,
            kind: KIND_ORDER.find(k => pool.some(p => p.kind === k)) || 'profile',
            read,
            aep: clustered.some(isAepDay),
            size: clustered.length > 1 ? 20 : count <= 2 ? 12 : count <= 5 ? 14 : 18,
        }
    })
}
